const LOCAL_SPECS_ASSET_ORIGIN = "http://localhost:9000/jameo-specs";
const LEGACY_SPECS_HOST_PREFIX = "specs.";

function getAssetPreviewKey(key: string) {
  return `__preview${key.charAt(0).toUpperCase()}${key.slice(1)}`;
}

export function getAssetPreviewValue<T extends object>(
  source: T | null | undefined,
  key: string,
  fallback?: string | null
) {
  if (source) {
    const previewKey = getAssetPreviewKey(key);
    const previewValue = (source as Record<string, unknown>)[previewKey];
    if (typeof previewValue === "string" && previewValue.trim()) {
      return previewValue;
    }
  }
  return fallback || "";
}

export function clearAssetPreviewValue<T extends object>(
  source: T | null | undefined,
  key: string
) {
  if (!source) {
    return;
  }

  delete (source as Record<string, unknown>)[getAssetPreviewKey(key)];
}

function getConfiguredSpecsAssetOrigin() {
  const configuredOrigin =
    process.env.REACT_APP_SPECS_ASSET_BASE_URL ||
    process.env.REACT_APP_SPECS_ASSET_URL ||
    "";

  if (configuredOrigin) {
    return configuredOrigin.replace(/\/$/, "");
  }

  if (typeof window !== "undefined") {
    const { hostname } = window.location;
    if (
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname === "::1"
    ) {
      return LOCAL_SPECS_ASSET_ORIGIN;
    }
  }

  const designStateUrl = process.env.REACT_APP_DESIGN_STATE_URL || "";
  if (!designStateUrl) return "";

  try {
    const parsed = new URL(
      designStateUrl,
      typeof window !== "undefined" ? window.location.origin : undefined
    );
    parsed.hostname = parsed.hostname.replace(/^generator\./, "specs.");
    parsed.pathname = "";
    parsed.search = "";
    parsed.hash = "";
    return parsed.toString().replace(/\/$/, "");
  } catch {
    return "";
  }
}

function rewriteLegacySpecsUrl(value: string, specsOrigin: string) {
  if (!specsOrigin) {
    return null;
  }

  const parsedValue = new URL(value);

  if (!parsedValue.hostname.startsWith(LEGACY_SPECS_HOST_PREFIX)) {
    return null;
  }

  const parsedOrigin = new URL(specsOrigin);
  const originPath = parsedOrigin.pathname.replace(/\/$/, "");
  parsedOrigin.pathname = `${originPath}${parsedValue.pathname}`;
  parsedOrigin.search = parsedValue.search;
  parsedOrigin.hash = parsedValue.hash;

  return parsedOrigin.toString();
}

export function resolveSpecsImageUrl(value?: string | null) {
  if (!value) return "";
  const trimmed = value.trim();
  if (!trimmed) return "";

  if (trimmed.startsWith("data:") || trimmed.startsWith("blob:")) {
    return trimmed;
  }

  if (trimmed.startsWith("s3::")) {
    return trimmed.slice("s3::".length);
  }

  const specsOrigin = getConfiguredSpecsAssetOrigin();

  if (/^https?:\/\//i.test(trimmed)) {
    return rewriteLegacySpecsUrl(trimmed, specsOrigin) || trimmed;
  }

  if (!specsOrigin) return trimmed;

  if (trimmed.startsWith("./data/")) {
    return `${specsOrigin}/${trimmed.slice("./data/".length)}`;
  }

  if (trimmed.startsWith("data/")) {
    return `${specsOrigin}/${trimmed.slice("data/".length)}`;
  }

  if (trimmed.startsWith("/data/")) {
    return `${specsOrigin}/${trimmed.slice("/data/".length)}`;
  }

  return trimmed;
}
